import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Check, ChevronDown, Clock3, Home, Mail, MessageSquare, RefreshCw, ShieldCheck, Smartphone, X } from 'lucide-react';
import { apiClient } from '../App';
import { Button } from '@/components/ui/button';
import Footer from '../components/Footer';
import {
  buildConfirmationExample,
  getEventCadence,
  getExceptionalQuietHours,
  getPreferenceLabel,
  getQuietHoursSummary,
  groupTransparencyEvents,
} from '../utils/notificationTransparencyView';

const CHANNELS = {
  in_app: { label: 'In-app', icon: Bell },
  email: { label: 'Email', icon: Mail },
  sms: { label: 'SMS', icon: MessageSquare },
  push: { label: 'Push', icon: Smartphone },
};
const CHANNEL_ORDER = ['in_app', 'push', 'email', 'sms'];
const EXAMPLE_STARTS = [
  { id: 'morning', label: 'Morning', startMinutes: 8 * 60, startLabel: '8:00 AM' },
  { id: 'afternoon', label: 'Afternoon', startMinutes: 14 * 60, startLabel: '2:00 PM' },
  { id: 'evening', label: 'Evening', startMinutes: 19 * 60, startLabel: '7:00 PM' },
];

const TREATMENT_LABELS = {
  not_delayed: 'not delayed',
  immediate: 'may send during quiet hours',
  deferred: 'delayed until quiet hours end',
  event_dependent: 'depends on the notification',
};

const ChannelAvailability = ({ event, channel }) => {
  const enabled = event.channels.includes(channel);
  return (
    <span data-channel={channel} data-enabled={enabled} className={`inline-flex items-center gap-1 text-xs font-medium ${enabled ? 'text-zinc-900' : 'text-zinc-400'}`}>
      {enabled ? <Check className="h-3.5 w-3.5 text-green-700" aria-hidden="true" /> : <X className="h-3.5 w-3.5" aria-hidden="true" />}
      <span className="sr-only">{CHANNELS[channel].label}: </span>{enabled ? 'Yes' : 'No'}
    </span>
  );
};

const ChannelBadges = ({ event }) => (
  <div className="flex flex-wrap gap-1.5" aria-label="Notification channels">
    {CHANNEL_ORDER.map(channel => {
      const config = CHANNELS[channel] || { label: channel, icon: Bell };
      const Icon = config.icon;
      const enabled = event.channels.includes(channel);
      return <span key={channel} data-channel-badge={channel} data-enabled={enabled} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${enabled ? 'border-zinc-200 bg-zinc-50 text-zinc-700' : 'border-zinc-100 bg-white text-zinc-400'}`}><Icon className="h-3 w-3" aria-hidden="true" />{config.label}<span className="sr-only">: {enabled ? 'Yes' : 'No'}</span></span>;
    })}
  </div>
);

const EventDetails = ({ row }) => {
  const exceptions = getExceptionalQuietHours(row);
  return (
    <div className="space-y-4 text-sm text-zinc-700">
      {row.events.map(event => (
        <div key={event.id} className="space-y-2">
          {row.events.length > 1 && <h4 className="font-semibold text-zinc-900">{event.name}</h4>}
          <dl className="grid gap-2 sm:grid-cols-2">
            <div><dt className="font-medium text-zinc-900">Trigger</dt><dd>{event.trigger}</dd></div>
            <div><dt className="font-medium text-zinc-900">Recipient</dt><dd>{event.recipient}</dd></div>
            <div><dt className="font-medium text-zinc-900">Cadence</dt><dd>{getEventCadence(event)}</dd></div>
            <div><dt className="font-medium text-zinc-900">Repeats</dt><dd>{event.repeats}</dd></div>
          </dl>
        </div>
      ))}
      {exceptions.length > 0 && <p><span className="font-medium text-zinc-900">Quiet-hours exception:</span> {exceptions.map(([channel]) => CHANNELS[channel]?.label || channel).join(', ')} may send immediately.</p>}
      <p><span className="font-medium text-zinc-900">Can it be disabled?</span> {row.can_disable}</p>
    </div>
  );
};

const DisclosureButton = ({ detailId, expanded, onToggle }) => (
  <button
    type="button"
    aria-expanded={expanded}
    aria-controls={detailId}
    onClick={onToggle}
    className="inline-flex min-h-9 items-center gap-1 rounded-md px-2 text-sm font-medium text-orange-700 hover:bg-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
  >
    <span>{expanded ? 'Hide' : 'Details'}</span>
    <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
  </button>
);

const NotificationTransparency = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [selectedExample, setSelectedExample] = useState('evening');

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await apiClient.get('/user/notification-transparency');
      setData(response.data);
    } catch (err) {
      console.error('Failed to load notification transparency:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rows = useMemo(() => groupTransparencyEvents(data?.events || []), [data]);
  const example = EXAMPLE_STARTS.find(item => item.id === selectedExample) || EXAMPLE_STARTS[2];
  const timeline = useMemo(() => buildConfirmationExample(
    data?.confirmation_timeline,
    example.startMinutes,
    data?.quiet_hours,
  ), [data, example.startMinutes]);
  const toggle = (id) => setExpandedIds(previous => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-zinc-50">
      <header className="sticky top-0 z-30 border-b border-zinc-200 bg-white" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <div className="mx-auto flex max-w-6xl items-center gap-1 px-4 pb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/notification-settings')} aria-label="Back to notification settings" className="h-10 w-10 rounded-full focus-visible:ring-2 focus-visible:ring-orange-500"><ArrowLeft className="h-5 w-5" /></Button>
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} aria-label="Dashboard" className="h-9 w-9 rounded-full focus-visible:ring-2 focus-visible:ring-orange-500"><Home className="h-4 w-4 text-zinc-500" /></Button>
          <h1 className="min-w-0 text-xl font-bold text-zinc-900 sm:text-2xl">How OpenRuns Notifications Work</h1>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
        {loading && <div className="rounded-xl border border-zinc-200 bg-white p-8 text-center text-zinc-600" role="status">Loading notification details…</div>}
        {!loading && error && <div className="rounded-xl border border-red-200 bg-white p-6 text-center" role="alert"><p className="font-medium text-zinc-900">Notification details could not be loaded.</p><Button variant="outline" onClick={load} className="mt-4 gap-2 focus-visible:ring-2 focus-visible:ring-orange-500"><RefreshCw className="h-4 w-4" />Try again</Button></div>}
        {!loading && !error && data && (
          <div className="space-y-7">
            <section aria-labelledby="at-a-glance" className="rounded-2xl border border-orange-200 bg-orange-50 p-4 sm:p-5">
              <h2 id="at-a-glance" className="text-lg font-bold text-zinc-900">At a glance</h2>
              <div className="mt-2 grid gap-x-6 gap-y-1 text-sm leading-6 text-zinc-700 md:grid-cols-2">
                <p><Clock3 className="mr-1 inline h-4 w-4 text-orange-700" aria-hidden="true" />Quiet hours: {data.quiet_hours.window}, using the {data.quiet_hours.timezone.toLowerCase()}.</p>
                {Object.entries(data.quiet_hours.channel_treatment || {}).map(([channel, treatment]) => <p key={channel}><span className="font-medium">{CHANNELS[channel]?.label || channel}:</span> {TREATMENT_LABELS[treatment] || treatment}.</p>)}
                <p>{data.at_a_glance.push_replaces_sms}</p>
                <p>{data.timing_note}</p>
              </div>
              <p className="mt-3 text-xs text-zinc-600">{data.quiet_hours.timezone_fallback}</p>
              <p className="mt-3 rounded-lg bg-amber-100/70 p-3 text-sm font-medium text-amber-950">{data.quiet_hours.delayed_reminder_note}</p>
            </section>

            {rows.length === 0 ? <div className="rounded-xl border border-zinc-200 bg-white p-8 text-center text-zinc-600">No notification details are available.</div> : (
              <section aria-labelledby="notification-matrix">
                <h2 id="notification-matrix" className="mb-3 text-xl font-bold text-zinc-900">Notification matrix</h2>
                <div className="hidden overflow-hidden rounded-xl border border-zinc-200 bg-white md:block">
                  <table className="w-full table-fixed text-left text-sm">
                    <thead className="bg-zinc-100 text-xs uppercase tracking-wide text-zinc-600"><tr><th className="w-[25%] px-3 py-3">Notification</th><th className="w-[7%] px-2 py-3">In-app</th><th className="w-[7%] px-2 py-3">Push</th><th className="w-[7%] px-2 py-3">Email</th><th className="w-[7%] px-2 py-3">SMS</th><th className="w-[15%] px-3 py-3">Quiet hours</th><th className="w-[19%] px-3 py-3">Preference</th><th className="w-[13%] px-2 py-3">Details</th></tr></thead>
                    <tbody className="divide-y divide-zinc-200">{rows.map(row => { const expanded = expandedIds.has(row.id); const detailId = `notification-details-desktop-${row.id}`; return [<tr key={row.id} className="align-middle"><th scope="row" className="px-3 py-2.5 font-semibold text-zinc-900">{row.name}{row.push_replaces_sms && <span className="ml-2 inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-800">Push replaces SMS</span>}</th><td className="px-2 py-2.5"><ChannelAvailability event={row} channel="in_app" /></td><td className="px-2 py-2.5"><ChannelAvailability event={row} channel="push" /></td><td className="px-2 py-2.5"><ChannelAvailability event={row} channel="email" /></td><td className="px-2 py-2.5"><ChannelAvailability event={row} channel="sms" /></td><td className="px-3 py-2.5 text-xs text-zinc-600">{getQuietHoursSummary(row)}</td><td className="px-3 py-2.5 text-xs text-zinc-600">{getPreferenceLabel(row)}</td><td className="px-2 py-2"><DisclosureButton detailId={detailId} expanded={expanded} onToggle={() => toggle(row.id)} /></td></tr>, expanded && <tr key={`${row.id}-details`}><td id={detailId} colSpan="8" className="bg-zinc-50 px-5 py-4"><EventDetails row={row} /></td></tr>]; })}</tbody>
                  </table>
                </div>

                <div className="space-y-2 md:hidden">{rows.map(row => { const expanded = expandedIds.has(row.id); const detailId = `notification-details-mobile-${row.id}`; return <article key={row.id} className="min-w-0 rounded-xl border border-zinc-200 bg-white p-3"><div className="flex min-w-0 items-start justify-between gap-2"><div className="min-w-0"><h3 className="break-words font-semibold text-zinc-900">{row.name}</h3><div className="mt-2"><ChannelBadges event={row} /></div></div><DisclosureButton detailId={detailId} expanded={expanded} onToggle={() => toggle(row.id)} /></div><div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-600"><span>{getQuietHoursSummary(row)}</span><span>{getPreferenceLabel(row)}</span>{row.push_replaces_sms && <span className="font-medium text-blue-800">Push replaces SMS</span>}</div>{expanded && <div id={detailId} className="mt-3 border-t border-zinc-100 pt-3"><EventDetails row={row} /></div>}</article>; })}</div>
              </section>
            )}

            {timeline.length > 0 && <section aria-labelledby="timeline-example" className="rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 id="timeline-example" className="text-lg font-bold text-zinc-900">Example: {example.label} training at {example.startLabel}</h2>
                <div className="grid grid-cols-3 gap-1 rounded-lg bg-zinc-100 p-1" aria-label="Choose a training time example">
                  {EXAMPLE_STARTS.map(option => <button key={option.id} type="button" aria-pressed={selectedExample === option.id} onClick={() => setSelectedExample(option.id)} className={`rounded-md px-2.5 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 ${selectedExample === option.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}>{option.label}</button>)}
                </div>
              </div>
              <ol className="mt-3 grid gap-2 sm:grid-cols-4">{timeline.map(item => <li key={`${item.displayTime}-${item.label}`} className="rounded-lg bg-zinc-50 p-3"><time className="text-sm font-bold text-orange-700">{item.displayTime}</time><p className="mt-1 text-sm text-zinc-700">{item.label}</p>{item.duringQuietHours && <p className="mt-2 text-xs font-medium text-indigo-700">Falls during quiet hours. {item.label === 'Unconfirmed spot may be released' ? 'The release action is not delayed by notification quiet hours; related notification delivery depends on its channel.' : 'Delivery treatment depends on the notification channel.'}</p>}</li>)}</ol>
              <p className="mt-3 text-xs text-zinc-500">Example derived from the current confirmation cadence.</p>
            </section>}
            <p className="flex items-start gap-2 text-xs text-zinc-500"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{data.quiet_hours.security_messages}</p>
          </div>
        )}
      </main>
      <Footer variant="slim" />
    </div>
  );
};

export default NotificationTransparency;
